import React from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import { formatDate } from '../../../../common/helpers/dateHelper';
import { SortType } from '../../../../common/enums/SortType';
import CustomBadge from '../../../../components/CustomBadge';
import Icon from '../../../../components/Icon';
import Tooltip from '../../../../components/Tooltip';
import ScoreStar from '../../../../components/ScoreStar';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { inactiveProgressMessage } from '../../../../common/helpers/dashboardHelper';
import { ICandidatePositionProfile } from '../../../../common/models/candidate/ICandidatePositionProfile';
import { IBindingCallback1 } from '../../../../common/models/callback';
import IconButton from '../../../../components/IconButton';

export const getColumns = (
  archive: IBindingCallback1<string>,
  unarchive: IBindingCallback1<string>,
  runReport: IBindingCallback1<string>
): Column[] => [
  {
    Header: () => <FormattedMessage id="candidateTable.candidateName" defaultMessage="Candidate name" />,
    id: 'name',
    width: 100,
    Cell: ({ row: { original } }: any) => (
      <div className="cell-active">{`${original.firstName} ${original.lastName}`}</div>
    ),
    sortType: SortType.Literal
  },
  {
    Header: () => <FormattedMessage id="Last activity" defaultMessage="Last activity" />,
    accessor: (item: any) => item.lastActivity && `${formatDate(item.lastActivity)}`,
    width: 80,
    id: 'lastActivity',
    sortType: SortType.Date
  },
  {
    Header: () => <FormattedMessage id="positionTable.progress" defaultMessage="Progress" />,
    id: 'referenceProgress',
    accessor: 'referenceProgress',
    Cell: ({ row: { original } }) => {
      const { totalReferences, referenceProgress, needsReview, status } = original as ICandidatePositionProfile;
      if (needsReview) {
        return (
          <div className="custom-badge red">
            <FormattedMessage id="needsReview" defaultMessage="Needs review" />
          </div>
        );
      }

      return (totalReferences
        ? (
          <CustomBadge min={1} max={totalReferences} value={referenceProgress} status={status}>
            {
              status !== CandidateStatus.Active ? inactiveProgressMessage(status)
                : (
                  <FormattedMessage id="complete" defaultMessage="Complete">
                    {message => `${referenceProgress} / ${totalReferences} ${message}`}
                  </FormattedMessage>
                )
            }
          </CustomBadge>
        )
        : (
          <CustomBadge min={1} value={0} max={2} status={status}>
            {
              status !== CandidateStatus.Active ? inactiveProgressMessage(status)
                : (
                  <FormattedMessage
                    id="candidateSidebarFilter.WaitingForCandidate"
                    defaultMessage="Waiting For Candidate"
                  />
                )
            }
          </CustomBadge>
        )
      );
    },
    sortType: SortType.Numeric
  },
  {
    Header: () => <FormattedMessage id="contactProfile.surveySent" defaultMessage="Survey sent" />,
    accessor: 'surveyName',
    id: 'survey',
    width: 65,
    Cell: ({ cell: { value } }) => (
      <div className="table-actions table-center" style={{ maxWidth: '75px' }}>
        <Tooltip content={value} trigger={['hover', 'focus']} placement="top">
          <Icon icon="survey" />
        </Tooltip>
      </div>
    )
  },
  {
    Header: () => <FormattedMessage id="contactProfile.score" defaultMessage="Score" />,
    id: 'avgScore',
    accessor: 'score',
    width: 50,
    Cell: ({ row: { original } }: any) => (
      <div className="table-center" style={{ maxWidth: '50px' }}>
        <ScoreStar score={original.score} />
      </div>
    ),
    sortType: SortType.Numeric
  },
  {
    Header: () => <FormattedMessage id="actions" defaultMessage="Actions" />,
    id: 'actions',
    width: 50,
    Cell: ({ row: { original } }) => {
      const { id, referenceProgress, status } = original as ICandidatePositionProfile;
      const isActive = status === CandidateStatus.Active;
      return (
        <div className="table-actions table-center" style={{ maxWidth: '50px' }}>
          <IconButton
            icon={<Icon icon="chart" />}
            onClick={() => runReport(id)}
            disabled={!referenceProgress}
            variant="blue"
            tooltip={{ id: 'runReport', defaultMessage: 'Run report' }}
          />
          <IconButton
            onClick={() => (isActive ? archive(id) : unarchive(id))}
            icon={<Icon icon={isActive ? 'archive' : 'unarchive'} />}
            variant="blue"
            tooltip={isActive
              ? { id: 'iconButtonLable.archive', defaultMessage: 'Archive' }
              : { id: 'iconButtonLable.unarchive', defaultMessage: 'Unarchive' }}
          />
        </div>
      );
    },
    disableSortBy: true,
    disableFilters: true
  }
];
